import React, { useCallback, useEffect, useRef, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import Logger from "../../../services/Logger";
import ConfirmDialog from "../../components/ConfirmDialog";
import CustomSnackbar from "../../components/CustomSnackbar";
import AdminJourneyMapService from "./AdminJourneyMapService";

const DEFAULT_TRACK = "ww_season_2026";
const TRACK_KEY = "admin.journeyMap.track";
const VIEW_MODE_KEY = "admin.journeyMap.view";
const BACKGROUND_SIZES = [
    { label: "1290×2796", width: 1290, height: 2796, urlKey: "imageUrl1290" },
    { label: "747×1620", width: 747, height: 1620, urlKey: "imageUrl747" },
    { label: "2796×1290", width: 2796, height: 1290, urlKey: "imageUrl2796" },
    { label: "1620×747", width: 1620, height: 747, urlKey: "imageUrl1620" },
];

function loadStoredTrack() {
    try {
        const stored = window.localStorage.getItem(TRACK_KEY);
        return stored && stored.trim() ? stored.trim() : DEFAULT_TRACK;
    } catch (error) {
        return DEFAULT_TRACK;
    }
}

function chapterTitleText(title) {
    if (!title) {
        return "";
    }
    return String(title).replace(/^\s*chapter\s+\d+\s*:\s*/i, "").trim();
}

function loadViewMode() {
    try {
        return window.localStorage.getItem(VIEW_MODE_KEY) === "list" ? "list" : "card";
    } catch (error) {
        return "card";
    }
}

const VIEW_ZOOM_MIN = 1;
const VIEW_ZOOM_MAX = 8;
const VIEW_ZOOM_STEP = 0.25;

function clampViewZoom(value) {
    const stepped = Math.round(value * 100) / 100;
    return Math.min(VIEW_ZOOM_MAX, Math.max(VIEW_ZOOM_MIN, stepped));
}

function centeredCrop(natural, requested) {
    const limit = Math.min(natural.w, natural.h);
    const size = Math.max(1, Math.min(limit, requested));
    return {
        x: Math.floor((natural.w - size) / 2),
        y: Math.floor((natural.h - size) / 2),
        size,
    };
}

function imageScale(natural, stage, zoom) {
    if (!natural || !stage || stage.w < 1 || stage.h < 1) {
        return 0;
    }
    return Math.min(stage.w / natural.w, stage.h / natural.h) * zoom;
}

export default function AdminJourneyMapMain({ embedded = false }) {
    const [trackId, setTrackId] = useState(loadStoredTrack);
    const [tracks, setTracks] = useState([]);
    const [loadingSummary, setLoadingSummary] = useState(false);
    const [summary, setSummary] = useState(null);
    const [viewMode, setViewMode] = useState(loadViewMode);
    const [trackTitleDraft, setTrackTitleDraft] = useState("");
    const [chapterTitleDrafts, setChapterTitleDrafts] = useState({});
    const [savingTrackTitle, setSavingTrackTitle] = useState(false);
    const [savingChapterNo, setSavingChapterNo] = useState(null);
    const [generatingNo, setGeneratingNo] = useState(null);
    const [regenerateOpen, setRegenerateOpen] = useState(false);
    const [sizePreview, setSizePreview] = useState(null);
    const [zoomChapter, setZoomChapter] = useState(null);
    const [zoomCrop, setZoomCrop] = useState(false);
    const [zoomNatural, setZoomNatural] = useState(null);
    const [crop, setCrop] = useState(null);
    const [cropping, setCropping] = useState(false);
    const [viewZoom, setViewZoom] = useState(1);
    const [viewPan, setViewPan] = useState({ x: 0, y: 0 });
    const [stageSize, setStageSize] = useState({ w: 0, h: 0 });
    const cropDragRef = useRef(null);
    const panDragRef = useRef(null);
    const [stageNode, setStageNode] = useState(null);
    const viewStateRef = useRef({});
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackMessage, setSnackMessage] = useState("");
    const [snackSeverity, setSnackSeverity] = useState("info");

    const showSnack = useCallback((message, severity = "info") => {
        setSnackMessage(message);
        setSnackSeverity(severity);
        setOpenSnackbar(true);
    }, []);

    const refreshSummary = useCallback(async (id) => {
        if (!id) {
            setSummary(null);
            return;
        }
        setLoadingSummary(true);
        try {
            const map = await AdminJourneyMapService.getMapSummary(id);
            setSummary(map);
            setTrackTitleDraft(map?.title || "");
            const drafts = {};
            const list = Array.isArray(map?.chapters) ? map.chapters : [];
            for (const chapter of list) {
                drafts[chapter.chapterNo] = chapterTitleText(chapter.title);
            }
            setChapterTitleDrafts(drafts);
        } catch (error) {
            Logger.warn("Journey map summary unavailable:", error);
            setSummary(null);
        } finally {
            setLoadingSummary(false);
        }
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const list = await AdminJourneyMapService.listTracks();
                if (!cancelled) {
                    setTracks(Array.isArray(list) ? list : []);
                }
            } catch (error) {
                Logger.warn("Journey tracks list failed:", error);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        refreshSummary(trackId);
    }, [trackId, refreshSummary]);

    const openView = (chapter, url, cropEnabled) => {
        if (!url) {
            showSnack("Image is not on this chapter yet", "warning");
            return;
        }
        setSizePreview(null);
        setZoomNatural(null);
        setCrop(null);
        setViewZoom(1);
        setViewPan({ x: 0, y: 0 });
        setZoomCrop(Boolean(cropEnabled));
        setZoomChapter({ ...chapter, viewUrl: url });
    };

    const openSized = (chapter, label, url) => {
        if (!url) {
            return;
        }
        if (label === "1536") {
            openView(chapter, chapter.imageMasterUrl, true);
            return;
        }
        if (String(label).includes("×")) {
            openView(chapter, url, false);
            return;
        }
        setSizePreview({
            chapterNo: chapter.chapterNo,
            title: chapter.title,
            label,
            url,
        });
    };

    const openOriginal = (chapter) => {
        openView(chapter, chapter?.imageMasterUrl, true);
    };

    viewStateRef.current = { viewZoom, viewPan, zoomNatural, stageSize };

    useEffect(() => {
        const node = stageNode;
        if (!node || !zoomChapter?.viewUrl) {
            return undefined;
        }
        const measure = () => {
            const w = node.clientWidth;
            const h = node.clientHeight;
            setStageSize((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(node);
        const onWheel = (event) => {
            event.preventDefault();
            const state = viewStateRef.current;
            if (!state.zoomNatural || state.stageSize.w < 1) {
                return;
            }
            const direction = event.deltaY < 0 ? 1 : -1;
            const current = state.viewZoom;
            const next = clampViewZoom(current + direction * VIEW_ZOOM_STEP);
            if (next === current) {
                return;
            }
            const rect = node.getBoundingClientRect();
            const cx = event.clientX - rect.left;
            const cy = event.clientY - rect.top;
            const oldScale = imageScale(state.zoomNatural, state.stageSize, current);
            const newScale = imageScale(state.zoomNatural, state.stageSize, next);
            const oldLeft = (state.stageSize.w - state.zoomNatural.w * oldScale) / 2 + state.viewPan.x;
            const oldTop = (state.stageSize.h - state.zoomNatural.h * oldScale) / 2 + state.viewPan.y;
            const newLeft = cx - ((cx - oldLeft) * newScale) / oldScale;
            const newTop = cy - ((cy - oldTop) * newScale) / oldScale;
            setViewZoom(next);
            setViewPan({
                x: newLeft - (state.stageSize.w - state.zoomNatural.w * newScale) / 2,
                y: newTop - (state.stageSize.h - state.zoomNatural.h * newScale) / 2,
            });
        };
        node.addEventListener("wheel", onWheel, { passive: false });
        return () => {
            observer.disconnect();
            node.removeEventListener("wheel", onWheel);
        };
    }, [stageNode, zoomChapter]);

    useEffect(() => {
        if (!zoomNatural || !zoomCrop) {
            setCrop(null);
            return;
        }
        setCrop(centeredCrop(zoomNatural, 1024));
    }, [zoomNatural, zoomCrop]);

    const onCropPointerDown = (event, type) => {
        if (!crop || cropping) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        event.currentTarget.setPointerCapture(event.pointerId);
        cropDragRef.current = {
            type,
            id: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            x: crop.x,
            y: crop.y,
            size: crop.size,
        };
    };

    const onCropPointerMove = (event) => {
        const drag = cropDragRef.current;
        if (!drag || drag.id !== event.pointerId || !zoomNatural) {
            return;
        }
        const scale = imageScale(zoomNatural, stageSize, viewZoom) || 1;
        const dx = (event.clientX - drag.startX) / scale;
        const dy = (event.clientY - drag.startY) / scale;
        if (drag.type === "move") {
            const maxX = Math.max(0, zoomNatural.w - drag.size);
            const maxY = Math.max(0, zoomNatural.h - drag.size);
            setCrop({
                x: Math.round(Math.min(maxX, Math.max(0, drag.x + dx))),
                y: Math.round(Math.min(maxY, Math.max(0, drag.y + dy))),
                size: drag.size,
            });
            return;
        }
        const delta = Math.round(Math.max(dx, dy));
        const minSize = Math.min(200, zoomNatural.w, zoomNatural.h);
        const maxSize = Math.min(zoomNatural.w - drag.x, zoomNatural.h - drag.y);
        const size = Math.min(maxSize, Math.max(minSize, drag.size + delta));
        setCrop({ x: drag.x, y: drag.y, size });
    };

    const onCropPointerUp = (event) => {
        if (cropDragRef.current?.id === event.pointerId) {
            cropDragRef.current = null;
        }
    };

    const changeViewZoom = (delta) => {
        const state = viewStateRef.current;
        if (!state.zoomNatural || state.stageSize.w < 1) {
            setViewZoom((current) => clampViewZoom(current + delta));
            return;
        }
        const current = state.viewZoom;
        const next = clampViewZoom(current + delta);
        if (next === current) {
            return;
        }
        const cx = state.stageSize.w / 2;
        const cy = state.stageSize.h / 2;
        const oldScale = imageScale(state.zoomNatural, state.stageSize, current);
        const newScale = imageScale(state.zoomNatural, state.stageSize, next);
        const oldLeft = (state.stageSize.w - state.zoomNatural.w * oldScale) / 2 + state.viewPan.x;
        const oldTop = (state.stageSize.h - state.zoomNatural.h * oldScale) / 2 + state.viewPan.y;
        const newLeft = cx - ((cx - oldLeft) * newScale) / oldScale;
        const newTop = cy - ((cy - oldTop) * newScale) / oldScale;
        setViewZoom(next);
        setViewPan({
            x: newLeft - (state.stageSize.w - state.zoomNatural.w * newScale) / 2,
            y: newTop - (state.stageSize.h - state.zoomNatural.h * newScale) / 2,
        });
    };

    const onPanPointerDown = (event) => {
        if (cropping || event.button !== 0) {
            return;
        }
        event.currentTarget.setPointerCapture(event.pointerId);
        panDragRef.current = {
            id: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            x: viewPan.x,
            y: viewPan.y,
        };
    };

    const onPanPointerMove = (event) => {
        const drag = panDragRef.current;
        if (!drag || drag.id !== event.pointerId) {
            return;
        }
        setViewPan({
            x: drag.x + (event.clientX - drag.startX),
            y: drag.y + (event.clientY - drag.startY),
        });
    };

    const onPanPointerUp = (event) => {
        if (panDragRef.current?.id === event.pointerId) {
            panDragRef.current = null;
        }
    };

    const handleCrop = async () => {
        if (!trackId || !zoomChapter || !crop || cropping) {
            return;
        }
        setCropping(true);
        try {
            const body = await AdminJourneyMapService.cropChapterImage(trackId, zoomChapter.chapterNo, crop);
            setSummary((prev) => {
                if (!prev?.chapters) {
                    return prev;
                }
                return {
                    ...prev,
                    chapters: prev.chapters.map((chapter) => (
                        chapter.chapterNo === zoomChapter.chapterNo ? { ...chapter, ...body } : chapter
                    )),
                };
            });
            setZoomChapter((prev) => (prev ? { ...prev, ...body } : prev));
            showSnack("Thumbnails updated from the selected area", "success");
        } catch (error) {
            Logger.error("Journey chapter crop failed:", error);
            showSnack(error?.message || "Could not update thumbnails", "error");
        } finally {
            setCropping(false);
        }
    };

    const handleTrackChange = (event) => {
        const next = event.target.value;
        setTrackId(next);
        try {
            window.localStorage.setItem(TRACK_KEY, next);
        } catch (error) {
            Logger.warn("Could not save journey track", error);
        }
    };

    const handleViewMode = (event, next) => {
        if (!next) {
            return;
        }
        setViewMode(next);
        try {
            window.localStorage.setItem(VIEW_MODE_KEY, next);
        } catch (error) {
            Logger.warn("Could not save journey view mode", error);
        }
    };

    const handleRenameTrack = async () => {
        const title = trackTitleDraft.trim();
        if (!trackId || !title || savingTrackTitle) {
            return;
        }
        setSavingTrackTitle(true);
        try {
            const body = await AdminJourneyMapService.renameTrack(trackId, title);
            setSummary((prev) => (prev ? { ...prev, title: body.title } : prev));
            setTrackTitleDraft(body.title || title);
            setTracks((prev) => prev.map((track) => (
                track.trackId === trackId ? { ...track, title: body.title } : track
            )));
            showSnack("Track name saved", "success");
        } catch (error) {
            Logger.error("Journey track rename failed:", error);
            showSnack(error?.message || "Could not rename the track", "error");
        } finally {
            setSavingTrackTitle(false);
        }
    };

    const handleRenameChapter = async (chapterNo) => {
        const title = (chapterTitleDrafts[chapterNo] || "").trim();
        if (!trackId || !title || savingChapterNo != null) {
            return;
        }
        setSavingChapterNo(chapterNo);
        try {
            const body = await AdminJourneyMapService.renameChapter(trackId, chapterNo, title);
            setSummary((prev) => {
                if (!prev?.chapters) {
                    return prev;
                }
                return {
                    ...prev,
                    chapters: prev.chapters.map((chapter) => (
                        chapter.chapterNo === chapterNo ? { ...chapter, title: body.title } : chapter
                    )),
                };
            });
            setChapterTitleDrafts((prev) => ({ ...prev, [chapterNo]: body.title || title }));
            setZoomChapter((prev) => (
                prev && prev.chapterNo === chapterNo ? { ...prev, title: body.title } : prev
            ));
            showSnack(`Chapter ${chapterNo} title saved`, "success");
        } catch (error) {
            Logger.error("Journey chapter rename failed:", error);
            showSnack(error?.message || "Could not rename the chapter", "error");
        } finally {
            setSavingChapterNo(null);
        }
    };

    const handleGenerate = async (chapterNo) => {
        if (!trackId || generatingNo != null) {
            return;
        }
        setGeneratingNo(chapterNo);
        try {
            const body = await AdminJourneyMapService.generateChapterImage(trackId, chapterNo);
            setSummary((prev) => {
                if (!prev?.chapters) {
                    return prev;
                }
                return {
                    ...prev,
                    chapters: prev.chapters.map((chapter) => (
                        chapter.chapterNo === chapterNo ? { ...chapter, ...body } : chapter
                    )),
                };
            });
            setZoomChapter((prev) => {
                if (!prev || prev.chapterNo !== chapterNo) {
                    return prev;
                }
                setZoomNatural(null);
                setCrop(null);
                setViewZoom(1);
                setViewPan({ x: 0, y: 0 });
                return { ...prev, ...body };
            });
            showSnack(`Chapter ${chapterNo} image is ready`, "success");
        } catch (error) {
            Logger.error("Journey chapter image failed:", error);
            showSnack(error?.message || "Image generation failed", "error");
        } finally {
            setGeneratingNo(null);
        }
    };

    const trackOptions = (() => {
        const ids = new Set([DEFAULT_TRACK, ...tracks.map((t) => t.trackId).filter(Boolean)]);
        if (trackId) {
            ids.add(trackId);
        }
        return Array.from(ids);
    })();

    const chapters = Array.isArray(summary?.chapters) ? summary.chapters : [];
    const viewScale = imageScale(zoomNatural, stageSize, viewZoom);
    const frameW = zoomNatural ? zoomNatural.w * viewScale : 0;
    const frameH = zoomNatural ? zoomNatural.h * viewScale : 0;
    const frameX = zoomNatural ? (stageSize.w - frameW) / 2 + viewPan.x : 0;
    const frameY = zoomNatural ? (stageSize.h - frameH) / 2 + viewPan.y : 0;

    return (
        <Box sx={{ p: embedded ? 0 : 2, maxWidth: embedded ? "none" : 1200 }}>
            {embedded ? null : (
                <Typography variant="h5" sx={{ mb: 1 }}>
                    Journey map
                </Typography>
            )}

            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={viewMode}
                    onChange={handleViewMode}
                    aria-label="Journey view"
                >
                    <ToggleButton value="card" aria-label="Card view">
                        <ViewModuleIcon fontSize="small" sx={{ mr: 0.5 }} />
                        Cards
                    </ToggleButton>
                    <ToggleButton value="list" aria-label="List view">
                        <ViewListIcon fontSize="small" sx={{ mr: 0.5 }} />
                        List
                    </ToggleButton>
                </ToggleButtonGroup>
                <FormControl size="small" sx={{ minWidth: 280 }}>
                    <InputLabel id="journey-track-label">Track</InputLabel>
                    <Select
                        labelId="journey-track-label"
                        label="Track"
                        value={trackId}
                        onChange={handleTrackChange}
                    >
                        {trackOptions.map((id) => {
                            const match = tracks.find((track) => track.trackId === id);
                            const label = match?.title && match.title !== id
                                ? `${id} — ${match.title}`
                                : id;
                            return (
                                <MenuItem key={id} value={id}>
                                    {label}
                                </MenuItem>
                            );
                        })}
                    </Select>
                </FormControl>
                {summary ? (
                    <>
                        <TextField
                            size="small"
                            label="Track name"
                            value={trackTitleDraft}
                            onChange={(event) => setTrackTitleDraft(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                    handleRenameTrack();
                                }
                            }}
                            sx={{ minWidth: 260, flex: "1 1 220px" }}
                        />
                        <Button
                            variant="outlined"
                            disabled={
                                savingTrackTitle
                                || !trackTitleDraft.trim()
                                || trackTitleDraft.trim() === (summary.title || "")
                            }
                            onClick={handleRenameTrack}
                            startIcon={savingTrackTitle ? <CircularProgress size={14} color="inherit" /> : null}
                        >
                            {savingTrackTitle ? "Saving…" : "Save name"}
                        </Button>
                    </>
                ) : null}
            </Box>

            <Box sx={{ mb: 2 }}>
                {loadingSummary ? (
                    <CircularProgress size={20} />
                ) : summary ? (
                    <Alert severity="info" sx={{ py: 0.5 }}>
                        <strong>{summary.title || summary.trackId}</strong>
                        {" · "}
                        {summary.trackId}
                        {" · "}
                        {chapters.length} chapters, maxLevel {summary.maxLevel}, pack{" "}
                        <code>{summary.packCode}</code>
                    </Alert>
                ) : (
                    <Alert severity="warning" sx={{ py: 0.5 }}>
                        This track is not in the database yet.
                    </Alert>
                )}
            </Box>

            {summary && viewMode === "list" ? (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: 72 }}>Chapter</TableCell>
                                <TableCell>Title</TableCell>
                                <TableCell sx={{ width: 140 }}>Levels</TableCell>
                                <TableCell sx={{ width: 120 }}>Status</TableCell>
                                <TableCell sx={{ width: 140 }} align="right">Image</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {chapters.map((chapter) => {
                                const busy = generatingNo === chapter.chapterNo;
                                const saving = savingChapterNo === chapter.chapterNo;
                                const thumb = chapter.imageUrl400 || chapter.imageUrl;
                                const storedTitle = chapterTitleText(chapter.title);
                                const draft = chapterTitleDrafts[chapter.chapterNo] ?? storedTitle;
                                const unchanged = draft.trim() === storedTitle;
                                return (
                                    <TableRow key={chapter.chapterNo} hover>
                                        <TableCell>{chapter.chapterNo}</TableCell>
                                        <TableCell>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <TextField
                                                    size="small"
                                                    value={draft}
                                                    onChange={(event) => {
                                                        const value = event.target.value;
                                                        setChapterTitleDrafts((prev) => ({
                                                            ...prev,
                                                            [chapter.chapterNo]: value,
                                                        }));
                                                    }}
                                                    onKeyDown={(event) => {
                                                        if (event.key === "Enter") {
                                                            handleRenameChapter(chapter.chapterNo);
                                                        }
                                                    }}
                                                    fullWidth
                                                />
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    disabled={saving || savingChapterNo != null || !draft.trim() || unchanged}
                                                    onClick={() => handleRenameChapter(chapter.chapterNo)}
                                                >
                                                    {saving ? "Saving…" : "Save"}
                                                </Button>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            {chapter.levelStart}–{chapter.levelEnd}
                                        </TableCell>
                                        <TableCell>{chapter.imageStatus || "pending"}</TableCell>
                                        <TableCell align="right">
                                            {chapter.imageMasterUrl ? (
                                                <Tooltip title="View full size" arrow>
                                                    <IconButton
                                                        size="small"
                                                        aria-label="View full size"
                                                        onClick={() => openOriginal(chapter)}
                                                    >
                                                        <ZoomInIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : thumb ? null : (
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    disabled={generatingNo != null}
                                                    onClick={() => handleGenerate(chapter.chapterNo)}
                                                    startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}
                                                >
                                                    {busy ? "Generating…" : "Generate"}
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : null}

            {summary && viewMode === "card" ? (
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                            gap: 2,
                        }}
                    >
                        {chapters.map((chapter) => {
                            const busy = generatingNo === chapter.chapterNo;
                            const thumb = chapter.imageUrl400 || chapter.imageUrl;
                            const hasImage = Boolean(thumb);
                            return (
                                <Paper key={chapter.chapterNo} sx={{ overflow: "hidden" }}>
                                    <Box sx={{ position: "relative", aspectRatio: "1", bgcolor: "action.hover" }}>
                                        {hasImage ? (
                                            <>
                                                <Box
                                                    component="img"
                                                    src={thumb}
                                                    alt={chapter.title || `Chapter ${chapter.chapterNo}`}
                                                    onClick={() => openOriginal(chapter)}
                                                    sx={{
                                                        width: "100%",
                                                        height: "100%",
                                                        objectFit: "cover",
                                                        display: "block",
                                                        cursor: chapter.imageMasterUrl ? "zoom-in" : "default",
                                                    }}
                                                />
                                                {chapter.imageMasterUrl ? (
                                                    <Tooltip title="View full size" arrow>
                                                        <IconButton
                                                            size="small"
                                                            aria-label="View full size"
                                                            onClick={() => openOriginal(chapter)}
                                                            sx={{
                                                                position: "absolute",
                                                                top: 8,
                                                                right: 8,
                                                                bgcolor: "rgba(255,255,255,0.92)",
                                                                boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
                                                                "&:hover": { bgcolor: "#fff" },
                                                            }}
                                                        >
                                                            <ZoomInIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                ) : null}
                                            </>
                                        ) : (
                                            <Box
                                                sx={{
                                                    height: "100%",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    p: 2,
                                                }}
                                            >
                                                <Button
                                                    variant="contained"
                                                    disabled={generatingNo != null}
                                                    onClick={() => handleGenerate(chapter.chapterNo)}
                                                    startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}
                                                >
                                                    {busy ? "Generating…" : "Generate"}
                                                </Button>
                                            </Box>
                                        )}
                                    </Box>
                                    <Box sx={{ p: 1.5 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                            {chapter.chapterNo}. {chapterTitleText(chapter.title)}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Levels {chapter.levelStart}–{chapter.levelEnd}
                                            {" · "}
                                            {chapter.imageStatus || "pending"}
                                        </Typography>
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
                                            {chapter.imageUrl ? (
                                                <Chip
                                                    size="small"
                                                    label="200"
                                                    clickable
                                                    onClick={() => openSized(chapter, "200", chapter.imageUrl)}
                                                />
                                            ) : null}
                                            {chapter.imageUrl400 ? (
                                                <Chip
                                                    size="small"
                                                    label="400"
                                                    clickable
                                                    onClick={() => openSized(chapter, "400", chapter.imageUrl400)}
                                                />
                                            ) : null}
                                            {chapter.imageMasterUrl ? (
                                                <Chip
                                                    size="small"
                                                    label="1536"
                                                    clickable
                                                    onClick={() => openSized(chapter, "1536", chapter.imageMasterUrl)}
                                                />
                                            ) : null}
                                            {BACKGROUND_SIZES.map((size) => {
                                                const url = chapter[size.urlKey];
                                                if (!url) {
                                                    return null;
                                                }
                                                return (
                                                    <Chip
                                                        key={size.label}
                                                        size="small"
                                                        label={size.label}
                                                        clickable
                                                        onClick={() => openSized(chapter, size.label, url)}
                                                    />
                                                );
                                            })}
                                        </Box>
                                    </Box>
                                </Paper>
                            );
                        })}
                    </Box>
            ) : null}

            <Dialog
                open={Boolean(sizePreview)}
                onClose={() => setSizePreview(null)}
                slotProps={{
                    paper: { sx: { width: "fit-content", maxWidth: "calc(100% - 32px)" } },
                }}
            >
                <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pr: 1 }}>
                    <Typography sx={{ flex: 1 }} noWrap>
                        {sizePreview
                            ? `${sizePreview.chapterNo}. ${chapterTitleText(sizePreview.title)} · ${sizePreview.label}`
                            : ""}
                    </Typography>
                    <IconButton aria-label="Close" onClick={() => setSizePreview(null)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent
                    sx={{
                        width: "100%",
                        boxSizing: "border-box",
                        display: "flex",
                        justifyContent: "center",
                        px: 3,
                        pb: 3,
                        pt: "16px !important",
                    }}
                >
                    {sizePreview ? (
                        <Box
                            component="img"
                            src={sizePreview.url}
                            alt=""
                            sx={{
                                display: "block",
                                width: "auto",
                                height: "auto",
                                maxWidth: String(sizePreview.label).includes("×") ? "min(420px, 90vw)" : "none",
                                maxHeight: String(sizePreview.label).includes("×") ? "80vh" : "none",
                            }}
                        />
                    ) : null}
                </DialogContent>
            </Dialog>

            <CustomSnackbar
                open={openSnackbar}
                setOpen={setOpenSnackbar}
                message={snackMessage}
                severity={snackSeverity}
            />

            <Dialog
                fullScreen
                open={Boolean(zoomChapter?.viewUrl)}
                onClose={() => setZoomChapter(null)}
                slotProps={{
                    paper: {
                        sx: {
                            display: "flex",
                            flexDirection: "column",
                            height: "100%",
                            maxHeight: "100%",
                        },
                    },
                }}
            >
                <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pr: 1, flexShrink: 0 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography noWrap>
                            {zoomChapter ? `${zoomChapter.chapterNo}. ${chapterTitleText(zoomChapter.title)}` : "Chapter image"}
                            {zoomNatural ? ` · ${zoomNatural.w}×${zoomNatural.h}` : ""}
                            {crop ? ` · crop ${crop.size}×${crop.size}` : ""}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {zoomCrop
                                ? "The whole picture fits the screen. Scroll or use the buttons to zoom, and drag the picture to move it. The bar on the square moves the crop."
                                : "The whole picture fits the screen. Scroll or use the buttons to zoom, and drag the picture to move it."}
                        </Typography>
                    </Box>
                    <Tooltip title="Zoom out">
                        <span>
                            <IconButton
                                aria-label="Zoom out"
                                disabled={viewZoom <= VIEW_ZOOM_MIN}
                                onClick={() => changeViewZoom(-VIEW_ZOOM_STEP)}
                            >
                                <ZoomOutIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Typography variant="caption" sx={{ minWidth: 40, textAlign: "center", fontWeight: 600 }}>
                        {Math.round(viewZoom * 100)}%
                    </Typography>
                    <Tooltip title="Zoom in">
                        <span>
                            <IconButton
                                aria-label="Zoom in"
                                disabled={viewZoom >= VIEW_ZOOM_MAX}
                                onClick={() => changeViewZoom(VIEW_ZOOM_STEP)}
                            >
                                <ZoomInIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Fit the whole image">
                        <span>
                            <IconButton
                                aria-label="Fit the whole image"
                                disabled={viewZoom === 1 && viewPan.x === 0 && viewPan.y === 0}
                                onClick={() => {
                                    setViewZoom(1);
                                    setViewPan({ x: 0, y: 0 });
                                }}
                            >
                                <RestartAltIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                    {zoomCrop ? (
                        <Button
                            size="small"
                            variant="outlined"
                            disabled={generatingNo != null || cropping}
                            onClick={() => {
                                if (!zoomChapter) {
                                    return;
                                }
                                setRegenerateOpen(true);
                            }}
                            startIcon={
                                generatingNo === zoomChapter?.chapterNo
                                    ? <CircularProgress size={14} color="inherit" />
                                    : null
                            }
                        >
                            {generatingNo === zoomChapter?.chapterNo ? "Regenerating…" : "Regenerate"}
                        </Button>
                    ) : null}
                    {zoomCrop ? (
                        <Button
                            size="small"
                            variant={crop?.size === 1024 ? "contained" : "outlined"}
                            disabled={!zoomNatural || cropping}
                            onClick={() => setCrop(centeredCrop(zoomNatural, 1024))}
                        >
                            1024×1024
                        </Button>
                    ) : null}
                    {zoomCrop ? (
                        <Button
                            size="small"
                            variant={crop?.size === 512 ? "contained" : "outlined"}
                            disabled={!zoomNatural || cropping}
                            onClick={() => setCrop(centeredCrop(zoomNatural, 512))}
                        >
                            512×512
                        </Button>
                    ) : null}
                    {zoomCrop ? (
                        <Button
                            variant="contained"
                            size="small"
                            disabled={!crop || cropping || generatingNo != null}
                            onClick={handleCrop}
                            startIcon={cropping ? <CircularProgress size={14} color="inherit" /> : null}
                        >
                            {cropping ? "Updating…" : "Update thumbnails"}
                        </Button>
                    ) : null}
                    <IconButton aria-label="Close" onClick={() => setZoomChapter(null)} disabled={cropping}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent
                    sx={{
                        flex: "1 1 auto",
                        display: "flex",
                        flexDirection: "column",
                        minHeight: 0,
                        p: 0,
                        overflow: "hidden",
                        bgcolor: "grey.900",
                    }}
                >
                    <Box
                        ref={setStageNode}
                        onPointerDown={onPanPointerDown}
                        onPointerMove={onPanPointerMove}
                        onPointerUp={onPanPointerUp}
                        onPointerCancel={onPanPointerUp}
                        sx={{
                            position: "relative",
                            flex: "1 1 auto",
                            width: "100%",
                            minHeight: "calc(100vh - 120px)",
                            overflow: "hidden",
                            cursor: panDragRef.current ? "grabbing" : "grab",
                            touchAction: "none",
                        }}
                    >
                        {zoomChapter?.viewUrl ? (
                            <Box
                                component="img"
                                src={zoomChapter.viewUrl}
                                alt={zoomChapter.title || "Chapter image"}
                                draggable={false}
                                onLoad={(event) => {
                                    setZoomNatural({
                                        w: event.target.naturalWidth,
                                        h: event.target.naturalHeight,
                                    });
                                }}
                                ref={(node) => {
                                    if (node && node.complete && node.naturalWidth > 0) {
                                        setZoomNatural((prev) => (
                                            prev && prev.w === node.naturalWidth && prev.h === node.naturalHeight
                                                ? prev
                                                : { w: node.naturalWidth, h: node.naturalHeight }
                                        ));
                                    }
                                }}
                                sx={{
                                    position: zoomNatural && viewScale > 0 ? "absolute" : "relative",
                                    left: zoomNatural && viewScale > 0 ? frameX : 0,
                                    top: zoomNatural && viewScale > 0 ? frameY : 0,
                                    width: zoomNatural && viewScale > 0 ? frameW : "100%",
                                    height: zoomNatural && viewScale > 0 ? frameH : "100%",
                                    objectFit: "contain",
                                    maxWidth: "none",
                                    maxHeight: "none",
                                    userSelect: "none",
                                    pointerEvents: "none",
                                }}
                            />
                        ) : null}
                        {crop && zoomNatural && viewScale > 0 ? (
                            <Box
                                sx={{
                                    position: "absolute",
                                    left: frameX + crop.x * viewScale,
                                    top: frameY + crop.y * viewScale,
                                    width: crop.size * viewScale,
                                    height: crop.size * viewScale,
                                    boxSizing: "border-box",
                                    border: "2px solid #fff",
                                    boxShadow: "0 0 0 9999px rgba(0,0,0,0.45)",
                                    pointerEvents: "none",
                                }}
                            >
                                <Box
                                    onPointerDown={(event) => onCropPointerDown(event, "move")}
                                    onPointerMove={onCropPointerMove}
                                    onPointerUp={onCropPointerUp}
                                    onPointerCancel={onCropPointerUp}
                                    sx={{
                                        position: "absolute",
                                        top: 6,
                                        left: "50%",
                                        transform: "translateX(-50%)",
                                        width: 44,
                                        height: 10,
                                        borderRadius: 1,
                                        bgcolor: "#fff",
                                        cursor: cropping ? "default" : "move",
                                        pointerEvents: "auto",
                                        touchAction: "none",
                                    }}
                                />
                                <Box
                                    onPointerDown={(event) => onCropPointerDown(event, "resize")}
                                    onPointerMove={onCropPointerMove}
                                    onPointerUp={onCropPointerUp}
                                    onPointerCancel={onCropPointerUp}
                                    sx={{
                                        position: "absolute",
                                        right: 4,
                                        bottom: 4,
                                        width: 16,
                                        height: 16,
                                        bgcolor: "#fff",
                                        border: "1px solid #333",
                                        cursor: "nwse-resize",
                                        pointerEvents: "auto",
                                        touchAction: "none",
                                    }}
                                />
                            </Box>
                        ) : null}
                    </Box>
                </DialogContent>
            </Dialog>
            <ConfirmDialog
                open={regenerateOpen}
                setOpen={setRegenerateOpen}
                title="Regenerate image"
                targetName={zoomChapter ? `${zoomChapter.chapterNo}. ${chapterTitleText(zoomChapter.title)}` : ""}
                message="The current picture will be replaced."
                targetId={zoomChapter?.chapterNo}
                okText="Regenerate"
                cancelText="Cancel"
                callback={handleGenerate}
            />
        </Box>
    );
}
